/**
 * The player's crawler library choices: which crawler is ACTIVE (the one in
 * play, and the one the GM Console shows first) and which are ARCHIVED.
 *
 * Stored as one small document on the player's account, beside the crawlers
 * (custom-content lane, name `crawler-library`), so the GM Console reads the
 * same answer the player sees. Pure functions over that state; nothing here
 * touches storage.
 *
 * Rules (owner, 2026-09-16):
 *  - a player may keep many crawlers; one at most is active;
 *  - an archived crawler cannot be active; archiving the active one clears it;
 *  - only an archived crawler can be deleted.
 */
export const LIBRARY_DOC = 'crawler-library';

export interface CrawlerLibraryState {
  version: 1;
  activeCharacterId: string | null;
  archivedIds: string[];
}

export function emptyLibraryState(): CrawlerLibraryState {
  return { version: 1, activeCharacterId: null, archivedIds: [] };
}

/** Tolerant read: anything unreadable becomes an empty library state. */
export function readLibraryState(raw: unknown): CrawlerLibraryState {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyLibraryState();
  const value = raw as Record<string, unknown>;
  const archived = Array.isArray(value.archivedIds) ? value.archivedIds.filter((id): id is string => typeof id === 'string') : [];
  const active = typeof value.activeCharacterId === 'string' && !archived.includes(value.activeCharacterId) ? value.activeCharacterId : null;
  return { version: 1, activeCharacterId: active, archivedIds: [...new Set(archived)] };
}

/** Drops references to crawlers that no longer exist. */
export function pruneLibraryState(state: CrawlerLibraryState, existingIds: readonly string[]): CrawlerLibraryState {
  const exists = new Set(existingIds);
  return {
    version: 1,
    activeCharacterId: state.activeCharacterId && exists.has(state.activeCharacterId) ? state.activeCharacterId : null,
    archivedIds: state.archivedIds.filter((id) => exists.has(id)),
  };
}

export type LibraryChange = { ok: true; state: CrawlerLibraryState } | { ok: false; reason: string };

export function setActiveCrawler(state: CrawlerLibraryState, id: string | null): LibraryChange {
  if (id !== null && state.archivedIds.includes(id)) {
    return { ok: false, reason: 'Restore this crawler from the archive before making it active.' };
  }
  return { ok: true, state: { ...state, activeCharacterId: id } };
}

export function archiveCrawler(state: CrawlerLibraryState, id: string): CrawlerLibraryState {
  return {
    version: 1,
    activeCharacterId: state.activeCharacterId === id ? null : state.activeCharacterId,
    archivedIds: state.archivedIds.includes(id) ? state.archivedIds : [...state.archivedIds, id],
  };
}

export function restoreCrawler(state: CrawlerLibraryState, id: string): CrawlerLibraryState {
  return { ...state, archivedIds: state.archivedIds.filter((a) => a !== id) };
}

export function canDelete(state: CrawlerLibraryState, id: string): string | null {
  return state.archivedIds.includes(id) ? null : 'Archive a crawler before deleting it.';
}

export function forgetCrawler(state: CrawlerLibraryState, id: string): CrawlerLibraryState {
  return {
    version: 1,
    activeCharacterId: state.activeCharacterId === id ? null : state.activeCharacterId,
    archivedIds: state.archivedIds.filter((a) => a !== id),
  };
}

export interface LibraryEntryView { id: string; name: string; level: number; floor: number; crawlerType: string }

/** Library tab and Archive tab contents, each sorted by name, active first in the library. */
export function partitionLibrary(entries: readonly LibraryEntryView[], state: CrawlerLibraryState) {
  const byName = (a: LibraryEntryView, b: LibraryEntryView) => a.name.localeCompare(b.name);
  const library = entries.filter((e) => !state.archivedIds.includes(e.id)).sort(byName);
  const active = library.find((e) => e.id === state.activeCharacterId);
  return {
    library: active ? [active, ...library.filter((e) => e.id !== active.id)] : library,
    archived: entries.filter((e) => state.archivedIds.includes(e.id)).sort(byName),
  };
}
